// Content Submission Page - full-page form for submitting or resubmitting content
// Shows brand feedback and original submission when content was rejected.

import { ContentSubmissionForm } from "@/components/content/content-submission-form";
import { FileDropSection } from "@/components/content/file-drop-section";
import { getRejectedGuidelines } from "@/components/content/content-detail-view";
import {
  ContentPreview,
  extractContentMedia,
} from "@/components/content/content-preview";
import {
  ChangesRequestedAlert,
  AwaitingPostAlert,
} from "@/components/content/content-status-alert";
import {
  ApprovedBadge,
  ChangesRequestedBadge,
  PendingApprovalBadge,
} from "@/components/content/content-status-badge";
import {
  getUnlinkedInstagramUsernames,
  isInstagramInsightsNotLinked,
} from "@/components/content/instagram-insights-banner";
import { InstagramStoryModal } from "@/components/content/instagram-story-modal";
import { captureEvent } from "@/lib/analytics-loader";
import {
  ContentState,
  getContentDisplayName,
  getPostTypeDisplayName,
  isInstagramStories,
  useContentRequirementsQuery,
  useInstagramStoriesQuery,
  useRefetchInstagramStories,
  type ContentRequirement,
  type InstagramStory,
} from "@/lib/queries/content";
import {
  useProjectInteractionsQuery,
  type CreatorInteraction,
} from "@/lib/queries/projects";
import {
  useBasicTermsQuery,
  useStandardBriefEnhancementsQuery,
} from "@/lib/queries/terms";
import { extractProjectIdAndToken } from "@/lib/url-utils";
import {
  Button,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Skeleton,
  toast,
} from "@repo/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronDown, Send } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { CampaignRouteContext } from "@/lib/types/route-context";

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/p/$profileSlug/c/$campaignId/submit-content/$interactionId/$contentReviewId",
)({
  component: ContentSubmitPage,
});

// ============================================================================
// Component
// ============================================================================

export function ContentSubmitPage() {
  const { interactionId, contentReviewId, orgSlug, profileSlug, campaignId } =
    Route.useParams();
  const context = Route.useRouteContext() as CampaignRouteContext;
  const { programId, profile, organization } = context;
  const navigate = useNavigate();

  // Instagram Story modal state
  const [isStoryModalOpen, setIsStoryModalOpen] = useState(false);
  const [selectedStories, setSelectedStories] = useState<InstagramStory[]>([]);

  // Collapsible original submission state
  const [originalOpen, setOriginalOpen] = useState(false);

  // Get interactions to find secureUrl
  const { data: interactionsData, isPending: interactionsPending } =
    useProjectInteractionsQuery(organization.id, programId, profile.id);

  const interaction = interactionsData?.interactions.find(
    (i: CreatorInteraction) => i.id === interactionId,
  );
  const secureUrl = interaction?.secureUrl ?? "";

  // Extract token from secureUrl for Instagram stories API
  const { projectId: externalProjectId, token: secureToken } =
    extractProjectIdAndToken(secureUrl);
  const externalPostProjectId = externalProjectId
    ? parseInt(externalProjectId, 10)
    : NaN;

  // Get content requirements
  const {
    data: contentData,
    isFetching: contentFetching,
    error: contentError,
  } = useContentRequirementsQuery(
    organization.id,
    secureUrl,
    profile.id,
    programId,
  );

  // Log content errors to console for debugging (without exposing details to users)
  useEffect(() => {
    if (contentError) console.error(contentError);
  }, [contentError]);

  const contentRequirements = contentData?.contentRequirements ?? [];

  // Find the specific requirement by contentReviewId
  const requirement = contentRequirements.find(
    (r: ContentRequirement) => String(r.id) === contentReviewId,
  );

  // Check whether the underlying brief has been cancelled. If so, we block the
  // form entirely — this defends the deep-link path where a creator lands on
  // this page directly via a saved URL or email reminder (EPD-7798).
  const { data: basicTermsData } = useBasicTermsQuery(
    organization.id,
    secureUrl,
  );
  const isBriefCanceled = basicTermsData?.terms?.canceled === true;

  // Get brief enhancements for clientId
  const { data: briefEnhancementsData } = useStandardBriefEnhancementsQuery(
    organization.id,
    secureUrl,
  );
  const clientId = briefEnhancementsData?.enhancements?.clientId ?? "";
  const requireInstagramInsights =
    briefEnhancementsData?.enhancements?.requireInstagramInsights ?? true;

  // Instagram story setup
  const igAccountId =
    requirement?.socialAccounts?.find(
      (account) => account.networkIdentifier?.toLowerCase() === "instagram",
    )?.id ?? requirement?.deliverable?.accountId;

  const { data: storiesData, isFetching: storiesFetching } =
    useInstagramStoriesQuery(
      organization.id,
      profile.id,
      igAccountId?.toString() ?? "",
      isNaN(externalPostProjectId) ? 0 : externalPostProjectId,
      secureToken ?? "",
      false,
    );

  const refetchStories = useRefetchInstagramStories(
    organization.id,
    profile.id,
    igAccountId?.toString() ?? "",
    isNaN(externalPostProjectId) ? 0 : externalPostProjectId,
    secureToken ?? "",
  );

  // Derived state
  const isLoading = interactionsPending || (!!secureUrl && contentFetching);
  const isRejected = requirement?.state === ContentState.REJECTED;
  const isApproved = requirement?.state === ContentState.ACCEPTED;

  const postType = requirement?.deliverable?.postType ?? null;
  const requiresApproval = requirement?.deliverable?.requiresApproval;
  const isStoryPostType = isInstagramStories(postType);
  const isStorySubmission =
    isStoryPostType && (!requiresApproval || isApproved);

  // For FLEXIBLE deliverables, Instagram Story may be one of the accepted
  // post types. The form handles the selection internally, but we need to
  // provide the story modal callback so it can open the picker.
  const acceptedPostTypes = requirement?.acceptedPostTypes ?? [];
  const isFlexibleWithStoryOption =
    postType?.toUpperCase() === "FLEXIBLE" &&
    acceptedPostTypes.some((pt: string) => isInstagramStories(pt));
  const canUseStoryModal = isStorySubmission || isFlexibleWithStoryOption;

  // Get original submission media and caption
  const mediaItems = requirement ? extractContentMedia(requirement) : [];
  const captionText =
    requirement?.content?.caption ?? requirement?.post?.text ?? null;
  const hasOriginalSubmission = mediaItems.length > 0 || !!captionText;

  // Get rejected guidelines
  const rejectedGuidelines = requirement
    ? getRejectedGuidelines(requirement)
    : [];

  // Navigation back to deliverable list
  const handleBack = useCallback(() => {
    navigate({
      to: "/o/$orgSlug/p/$profileSlug/c/$campaignId/submit-content/$interactionId",
      params: { orgSlug, profileSlug, campaignId, interactionId },
    });
  }, [navigate, orgSlug, profileSlug, campaignId, interactionId]);

  // Handle submission success
  const handleSubmissionSuccess = useCallback(() => {
    captureEvent("content_submitted", {
      organizationId: organization.id,
      interactionId,
    });
    toast.success("Content submitted successfully");
    handleBack();
  }, [organization.id, interactionId, handleBack]);

  // Story modal handlers
  const handleRequestStorySelection = useCallback(() => {
    setIsStoryModalOpen(true);
  }, []);

  const handleSelectStories = useCallback((stories: InstagramStory[]) => {
    setSelectedStories(stories);
    setIsStoryModalOpen(false);
  }, []);

  const handleUpdateStories = useCallback(async () => {
    try {
      await refetchStories();
      toast.success("Stories refreshed");
    } catch {
      toast.error("Failed to refresh stories");
    }
  }, [refetchStories]);

  // Loading state
  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-7 w-48" />
        </div>
        <Card>
          <CardContent className="pt-6 space-y-4">
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-40 w-full rounded-lg" />
            <Skeleton className="h-10 w-32" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error / not found states
  if (!interaction || !secureUrl) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              Could not find interaction data. Please go back and try again.
            </p>
            <Button variant="outline" className="mt-4" asChild>
              <Link
                to="/o/$orgSlug/p/$profileSlug/c/$campaignId"
                params={{ orgSlug, profileSlug, campaignId }}
              >
                Back to Campaign
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (contentError) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-destructive">
              Something went wrong loading content requirements. Please try
              again.
            </p>
            <Button variant="outline" className="mt-4" onClick={handleBack}>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!requirement) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              Deliverable not found. It may have been removed.
            </p>
            <Button variant="outline" className="mt-4" onClick={handleBack}>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isBriefCanceled) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="font-medium">This brief has been cancelled.</p>
            <p className="text-sm text-muted-foreground mt-2">
              You can no longer submit content for this brief. Please reach out
              to the brand if you have any questions.
            </p>
            <Button variant="outline" className="mt-4" asChild>
              <Link
                to="/o/$orgSlug/p/$profileSlug/c/$campaignId"
                params={{ orgSlug, profileSlug, campaignId }}
              >
                Back to Campaign
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const displayName = getContentDisplayName(requirement);
  const postTypeDisplay = getPostTypeDisplayName(postType);

  // Status badge — only show when content has been submitted
  const isPendingApproval =
    requiresApproval &&
    (requirement.state === ContentState.UPLOADED ||
      requirement.state === ContentState.VALIDATING_UPLOAD ||
      requirement.state === ContentState.AMENDED ||
      requirement.state === ContentState.GCR_PENDING);
  const statusBadge = isRejected ? (
    <ChangesRequestedBadge />
  ) : isApproved ? (
    <ApprovedBadge />
  ) : isPendingApproval ? (
    <PendingApprovalBadge />
  ) : null;

  return (
    <>
      <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-6 overflow-x-hidden">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold">{displayName}</h1>
            {statusBadge}
          </div>
          {postType !== "FLEXIBLE" && (
            <p className="text-sm text-muted-foreground mt-1">
              {postTypeDisplay}
            </p>
          )}
        </div>

        {/* Changes Requested Alert */}
        {isRejected && (
          <ChangesRequestedAlert
            brandComment={requirement.brandRejectionComment}
            rejectedGuidelines={rejectedGuidelines}
          />
        )}

        {/* Awaiting Post Alert (approved state) */}
        {isApproved && requiresApproval && (
          <AwaitingPostAlert
            contentSubmissionDate={requirement.dateFirstUpload}
            contentApprovalDate={requirement.dateApproved}
          />
        )}

        {/* Original Submission (collapsible, shown when content was previously submitted) */}
        {hasOriginalSubmission && (isRejected || isApproved) && (
          <Collapsible open={originalOpen} onOpenChange={setOriginalOpen}>
            <Card>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-2 w-full p-4 text-left hover:bg-muted/50 transition-colors rounded-t-lg"
                >
                  <Send className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium flex-1">
                    Original Submission
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${
                      originalOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 pb-4">
                  <ContentPreview media={mediaItems} caption={captionText} />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        {/* Submission Form. The heading renders inside the form's drop area, so a
            file dropped on it uploads; the wrapper stops a drop on the card
            padding from opening the file in a new tab (EPD-12704). */}
        <FileDropSection>
          <Card>
            <CardContent className="pt-6">
              <ContentSubmissionForm
                heading={
                  <h2 className="text-base font-medium">
                    {isRejected
                      ? "Upload Revised Content"
                      : isApproved
                        ? "Submit Post Link"
                        : "Submit Content"}
                  </h2>
                }
                requirement={requirement}
                organizationId={organization.id}
                profileId={profile.id}
                programId={programId}
                secureUrl={secureUrl}
                clientId={clientId}
                onSuccess={handleSubmissionSuccess}
                onCancel={handleBack}
                selectedStories={selectedStories}
                instagramHandle={storiesData?.username}
                onRequestStorySelection={
                  canUseStoryModal ? handleRequestStorySelection : undefined
                }
                requireInstagramInsights={requireInstagramInsights}
              />
            </CardContent>
          </Card>
        </FileDropSection>
      </div>

      {/* Instagram Story Selection Modal */}
      {canUseStoryModal && (
        <InstagramStoryModal
          open={isStoryModalOpen}
          onOpenChange={setIsStoryModalOpen}
          stories={storiesData?.stories ?? []}
          loading={storiesFetching}
          brandInstagramUsername={requirement.brandInstagramUsername}
          onSelectStories={handleSelectStories}
          onUpdateClick={handleUpdateStories}
          insightsNotLinked={isInstagramInsightsNotLinked(
            requirement.socialAccounts,
          )}
          unlinkedAccountUsernames={getUnlinkedInstagramUsernames(
            requirement.socialAccounts,
          )}
        />
      )}
    </>
  );
}
